import { signUpSchema, type SignUpInput } from '@church/domain'
import { submitSafely, useSignUp } from '@church/data'
import { faUserPlus } from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, ErrorNote, Field, Input } from '../components/ui.tsx'
import { afterSignIn, isExternal } from '../lib/site.ts'

export const Route = createFileRoute('/_auth/sign-up')({
  validateSearch: z.object({ redirect: z.string().optional() }),
  component: SignUpScreen,
})

function SignUpScreen() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const signUp = useSignUp()
  const form = useForm<SignUpInput>({ resolver: zodResolver(signUpSchema) })

  const submit = form.handleSubmit(async (input) => {
    // better-auth signs the new user in and mails a verification link; the
    // shell shows a banner until the address is confirmed.
    await signUp.mutateAsync(input)
    // Where they were headed (an invitation, another app), else their first church.
    const target = afterSignIn(search.redirect, '/onboarding')
    if (isExternal(target)) window.location.assign(target)
    else await navigate({ to: target })
  })

  return (
    <form onSubmit={submitSafely(submit)} className="space-y-4" noValidate>
      <h1 className="text-xl font-semibold text-content-default">Create your account</h1>

      <Field label="Name" error={form.formState.errors.name?.message}>
        <Input autoComplete="name" {...form.register('name')} />
      </Field>

      <Field label="Email" error={form.formState.errors.email?.message}>
        <Input type="email" autoComplete="email" {...form.register('email')} />
      </Field>

      <Field label="Password" error={form.formState.errors.password?.message}>
        <Input type="password" autoComplete="new-password" {...form.register('password')} />
      </Field>

      <ErrorNote error={signUp.error} />

      <Button type="submit" icon={faUserPlus} className="w-full" disabled={signUp.isPending}>
        {signUp.isPending ? 'Creating account…' : 'Create account'}
      </Button>

      <p className="text-center text-sm text-content-muted">
        Already have an account?{' '}
        <Link
          to="/sign-in"
          search={{ redirect: search.redirect }}
          className="text-content-brand hover:underline"
        >
          Sign in
        </Link>
      </p>
    </form>
  )
}
